import { useCallback, useState } from 'react';
import { useNavigate, generatePath, useLocation } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  DocumentStatus,
  PermissionEnum,
  DocumentType,
  Document,
  BaseTransaction,
  Currency,
  formatBalance,
  formatDate,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useAddCommentToProblemTransactionDocumentMutation,
  useAddCommentToProblemTransactionMutation,
  useAddDocumentToProblemTransactionMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useCreateProblemTransactionChatMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useDeleteCommentInProblemTransactionMutation,
  useDeleteProblemTransactionDocumentsMutation,
  useEditCommentInProblemTransactionDocumentMutation,
  useEditCommentInProblemTransactionMutation,
  useLazyGetCustomerIncomingTransactionQuery,
  useLazyGetCustomerIncomingTransactionWarningQuery,
  useLazyGetCustomerIncomingTransactionWarningsQuery,
  useLazyGetCustomersAccountBalanceQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionCommentsQuery,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useLazyGetTransactionAmlQuery,
  useLazyGetTransactionStatementsChangeLogsQuery,
  useUploadDocumentMutation,
} from '../../../../customers/store/services/customers';
import {
  changePanelState,
  setBlacklistData,
  setBlacklistDataItem,
  setBlacklistDataVisible,
  setBlacklistItemVisible,
  setPanelIsOpen,
} from '../../../store/slices/problem-transactions/incoming-transaction';
import { PspAccount } from '../../../../../store/dictionaries/slice';
import { UploadResponse } from '../../../../../store/documents/types';
import { Employee } from '../../../../../store/dictionaries/types';
import { Balance } from '../../../types';

const useProblemIncomingTransaction = ({
  transactionId,
  activePermissions,
  documents,
  document: transactionDocument,
  backPath,
  customerId,
  transaction,
  pspAccounts,
  currentEmployee,
  currenciesList,
  sendMessage,
}: {
  transactionId?: string;
  activePermissions?: PermissionEnum[];
  documents?: Document[];
  document?: Document;
  backPath?: string;
  customerId?: string;
  transaction?: BaseTransaction;
  pspAccounts?: PspAccount[];
  currentEmployee?: Employee;
  currenciesList?: Currency[];
  sendMessage?: SendMessage | null;
}) => {
  const [imgLink, setImgLink] = useState('');
  const [accountBalance, setAccountBalance] = useState<number>();
  const [pspPartnerAccountBalance, setPspPartnerAccountBalance] =
    useState<number>();
  const [balances, setBalances] = useState<Balance[]>();
  const [pspPartnerAccountBalanceInfo, setPspPartnerAccountBalanceInfo] =
    useState<Balance>();
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [getIncomingAml, { isLoading: isLoadingAml }] =
    useLazyGetTransactionAmlQuery();
  const [getIncomingLogs, { isLoading: isLoadingLog }] =
    useLazyGetCustomersTransactionLogsQuery();
  const [getChats] = useLazyGetProblemTransactionChatsQuery();
  const [
    getCustomerIncomingTransactionWarningsQuery,
    { isLoading: warningsIsLoading },
  ] = useLazyGetCustomerIncomingTransactionWarningsQuery();
  const [
    getCustomerIncomingTransaction,
    { isLoading: transactionIsLoading, isFetching },
  ] = useLazyGetCustomerIncomingTransactionQuery();
  const [getTransactionDocuments, { isFetching: documentsIsFetching }] =
    useLazyGetProblemTransactionsDocumentsQuery();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetProblemTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteProblemTransactionDocumentsMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeProblemTransactionDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeProblemTransactionDocumentStatusMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentToProblemTransactionDocumentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInProblemTransactionDocumentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentInProblemTransactionDocumentMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionStatementsChangeLogsQuery();
  const [
    getAccountBalance,
    { isLoading: balanceIsLoading, isFetching: balanceIsFetching },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [
    getPspPartnerBalance,
    {
      isLoading: pspPartnerBalanceisLoading,
      isFetching: pspPartnerBalanceisFetching,
    },
  ] = useLazyGetCustomersAccountBalanceQuery();
  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();
  const [getRecipientAccounts] =
    useLazyGetProblemTransactionRecipientClientsOfficiasQuery();
  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetProblemTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToProblemTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInProblemTransactionMutation();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInProblemTransactionMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useUploadDocumentMutation();
  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddDocumentToProblemTransactionMutation();
  const [
    getIncomingTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerIncomingTransactionWarningQuery();

  const getIncomingTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    await getIncomingTransactionWarningById({
      id,
      warningId,
    });
  };

  const createChatWithMessage = async ({
    message,
    userId,
  }: {
    message: string;
    userId?: string;
  }) => {
    const title = `RFI: Incoming transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId: transaction?.recipientAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setUploadedFilesError([]);
    const nextUploadedFilesErrors: string[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const response = (await upload(files[file as any])) as unknown as {
          data?: UploadResponse[];
          error?: {
            originalStatus: number;
            data?: {
              message?: string;
            };
          };
          message?: string;
        };

        let nextFile;

        if (response && response.data) {
          nextFile = { ...response.data[0] };

          let fileName = fileItem?.name;

          if (
            response.data[0] &&
            response.data[0]?.mimeType?.includes('image')
          ) {
            fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
          }

          nextFile.name = fileName;
        } else if (response.error) {
          if (response.error.originalStatus === 413) {
            nextUploadedFilesErrors.push(
              `${files[file as any].name} size too large`,
            );
          } else {
            // eslint-disable-next-line no-lonely-if
            if (response?.error?.data?.message) {
              nextUploadedFilesErrors.push(response?.error?.data?.message);
            }
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);

    if (filesResponse && filesResponse.length) {
      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
              organizationId: organizationId as string,
            };

            const response = await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            });

            return response;
          }),
        );
      }
    }
  };

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-incoming`,
      body: {
        text,
      },
    });
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-incoming`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-incoming`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-incoming`,
      cursor: cursor || '',
    });
  };

  const onCreateTransactionChat = (userIds?: string[]) => {
    const title = `RFI: Incoming transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    if (!userIds?.length) {
      createTransactionChat({
        id: transactionId as string,
        body: {
          title,
          employeeId: currentEmployee?.id as string,
          organizationId: transaction?.recipientAccount?.organizationId,
        },
      });
    } else {
      Promise.all(
        userIds.map((item) =>
          createTransactionChat({
            id: transactionId as string,
            body: {
              title,
              employeeId: currentEmployee?.id as string,
              organizationId: transaction?.recipientAccount?.organizationId,
              userId: item,
            },
          }),
        ),
      );
    }
  };

  const getIncomingLogsAction = useCallback(() => {
    getIncomingLogs(transactionId as string);
  }, [transactionId]);

  const getAccountBalanceAction = useCallback(async () => {
    const response = (await getAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.currencyId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setAccountBalance(response.data.data.edges[0].node.value);
      setBalances(response.data.data.edges?.map((item) => item.node));
    }

    const pspPartnerAccountId = pspAccounts?.find(
      (item) =>
        item.account?.fullNumber === transaction?.pspPartnerAccountNumber,
    )?.account?.id;

    if (pspPartnerAccountId) {
      const pspResponse = (await getPspPartnerBalance({
        id: pspPartnerAccountId,
        currencyId: transaction?.currencyId as string,
      })) as { data: { data: { edges: { node: Balance }[] } } };

      if (pspResponse.data) {
        setPspPartnerAccountBalance(pspResponse.data.data.edges[0].node.value);
        setPspPartnerAccountBalanceInfo(pspResponse.data.data.edges[0].node);
      }
    }
  }, [transaction]);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const getIncomingAmlAction = useCallback(() => {
    getIncomingAml(transactionId as string);
  }, [transactionId]);

  const getIncomingTransactionById = useCallback(() => {
    getCustomerIncomingTransaction(transactionId as string);
    getIncomingAml(transactionId as string);
    getCustomerIncomingTransactionWarningsQuery(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INCOMING_LIST,
      )
    ) {
      getIncomingLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }

    if (
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)
    ) {
      getTransactionDocuments({ id: transactionId as string });
    }
  }, [transactionId, activePermissions]);

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: transactionDocument?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  const handleClose = () => {
    if (backPath) {
      dispatch(setPanelIsOpen(false));

      setTimeout(() => {
        navigate(
          generatePath(`${backPath}${search || ''}`, {
            customerId,
          }),
        );
      }, 100);
    }
  };

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(setBlacklistItemVisible(false));
    dispatch(setBlacklistDataItem(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  return {
    handleClose,
    transactionIsLoading,
    isLoadingAml,
    isLoadingLog,
    warningsIsLoading,
    isFetching,
    downloadInProgress,
    downloadIsFetching,
    imgLink,
    onDeleteDocument,
    deleteDocumentInProgress,
    deleteDocumentIsSuccess,
    onMoveDocument,
    documentCategoryChangeInProgress,
    onDownloadDocument,
    onSendDocumentComment,
    onEditComment,
    editCommentInProgress,
    editCommentIsSuccess,
    onDeleteComment,
    deleteCommentInProgress,
    commentInProgress,
    documentStatusChangeInProgress,
    onChangeCustomerDocumentsStatus,
    downloadFileIsSuccess,
    getTransactionDocumentyIdAction,
    documentIsLoading,
    documentIsFetching,
    handleCloseBlacklistData,
    getIncomingAmlAction,
    getChangeLogsAction,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getAccountBalanceAction,
    balanceIsLoading,
    pspPartnerBalanceisLoading,
    balanceIsFetching,
    pspPartnerBalanceisFetching,
    accountBalance,
    pspPartnerAccountBalance,
    balances,
    pspPartnerAccountBalanceInfo,
    getIncomingLogsAction,
    getRecipientAccounts,
    onCreateTransactionChat,
    chatCreateInProgress,
    getTransactionCommentsAction,
    commentsIsLoading,
    commentsIsFetching,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
    selectedDocumentId,
    setSelectedDocumentId,
    setUploadedFilesError,
    onUploadDocument,
    fileUploadInProgress,
    addDocumentInProgress,
    fileUploadIsSuccess,
    uploadedFilesError,
    documentsIsFetching,
    getTransationDocumentsAction,
    createChatWithMessage,
    getIncomingTransactionWarningByIdAction,
    isWarningByIdLoading,
    getIncomingTransactionById,
    isWarningByIdFetching,
  };
};

export default useProblemIncomingTransaction;
